import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';

import { ProfileView } from './ProfileView';
import { BrainApi } from '@src/services/brain/brain-api';
import { LutienApi } from '@src/services/lutien/lutien-api';
import { getAuthToken } from '@src/services/auth-service';
import { GetMeResponseDTO } from '@src/services/brain/modules/v1/auth/dto/brain-auth.getMe.response.dto';

export const ProfileContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState<
    GetMeResponseDTO & {
      organizations: {
        id: string;
        fantasyName: string;
      }[];
    }
  >(null);
  const [signatureKey, setSignatureKey] = useState<string | null>(null);
  const [isLoadingMe, setIsLoadingMe] = useState(true);
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);
  const [isRefreshingSignature, setIsRefreshingSignature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [organizationsDisplayLimit, setOrganizationsDisplayLimit] = useState(10);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setIsLoadingMe(true);
    const brainApi = new BrainApi();
    const lutienApi = new LutienApi();

    const me = await brainApi.v1.auth.getMe();

    const allOrganizations = await lutienApi.v1.organizations.get();

    const orgDictionary: Record<string, string> = {};
    allOrganizations.forEach((org) => {
      orgDictionary[org.id] = org.fantasyName;
    });

    const organizations = me.organizationIds
      .filter((orgId) => orgDictionary[orgId])
      .map((orgId) => ({
        id: orgId,
        fantasyName: orgDictionary[orgId],
      }))
      .sort((a, b) => a.fantasyName.localeCompare(b.fantasyName));

    setUserData({
      ...me,
      organizations,
    });
    setIsLoadingMe(false);
  };

  const handleGetSignature = async () => {
    try {
      setIsLoadingSignature(true);
      setError(null);
      const brainApi = new BrainApi();
      const response = await brainApi.v1.auth.signatures.get();
      setSignatureKey(response.signatureKey);
      enqueueSnackbar('Signature key retrieved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to get signature:', err);
      setError('Failed to retrieve signature key');
      enqueueSnackbar('Failed to retrieve signature key', { variant: 'error' });
    } finally {
      setIsLoadingSignature(false);
    }
  };

  const handleRefreshSignature = async () => {
    try {
      setIsRefreshingSignature(true);
      setError(null);
      const brainApi = new BrainApi();
      const response = await brainApi.v1.auth.signatures.post();
      setSignatureKey(response.signatureKey);
      setShowSignature(true);
      enqueueSnackbar('New signature key generated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Failed to refresh signature:', err);
      setError('Failed to generate new signature key');
      enqueueSnackbar('Failed to generate new signature key', { variant: 'error' });
    } finally {
      setIsRefreshingSignature(false);
    }
  };

  const handleCopySignature = () => {
    if (signatureKey) {
      navigator.clipboard.writeText(signatureKey);
      enqueueSnackbar('Signature key copied to clipboard', { variant: 'success' });
    }
  };

  const handleToggleSignatureVisibility = () => {
    setShowSignature(!showSignature);
  };

  const handleLoadMoreOrganizations = () => {
    setOrganizationsDisplayLimit((prev) => prev + 10);
  };

  const handleCopyToken = async () => {
    const token = await getAuthToken();
    if (token) {
      navigator.clipboard.writeText(token);
      enqueueSnackbar('Bearer token copied to clipboard', { variant: 'success' });
    } else {
      enqueueSnackbar('No valid token available', { variant: 'warning' });
    }
  };

  return (
    <ProfileView
      userData={userData}
      signatureKey={signatureKey}
      isLoadingMe={isLoadingMe}
      isLoadingSignature={isLoadingSignature}
      isRefreshingSignature={isRefreshingSignature}
      error={error}
      showSignature={showSignature}
      organizationsDisplayLimit={organizationsDisplayLimit}
      onGetSignature={handleGetSignature}
      onRefreshSignature={handleRefreshSignature}
      onCopySignature={handleCopySignature}
      onToggleSignatureVisibility={handleToggleSignatureVisibility}
      onLoadMoreOrganizations={handleLoadMoreOrganizations}
      onCopyBearer={handleCopyToken}
    />
  );
};
